import { useCallback, useRef, useSyncExternalStore } from "react"
import { liveQuery } from "dexie"
import type { Observable } from "dexie"
import { getDbEpoch, subscribeDbEpoch } from "./db-epoch"

/**
 * Module-level shared `liveQuery` cache.
 *
 * `dexie-react-hooks`' `useLiveQuery` creates a NEW Dexie `Observable` per
 * hook instance — when a component unmounts (virtualizer evicts it, a
 * collapsible closes, a route changes), the subscription tears down and a
 * remount fires a fresh IDB scan from `undefined`. For the recap that
 * means scrolling a partner card off-screen + back, or collapsing /
 * re-expanding a section, costs another roundtrip even though the rows
 * haven't moved.
 *
 * This module holds a single shared `Observable` + cached value + React
 * subscriber set per `key`. Components attach via `useSharedLiveQuery`,
 * read the cached value SYNCHRONOUSLY on subsequent renders, and
 * subscribe for emits via React's `useSyncExternalStore`. Unmounting
 * decrements the ref count but does not destroy the entry immediately —
 * a grace window keeps it warm so a quick remount snaps back to cached
 * data with zero IDB work. Destruction happens only if no subscriber
 * attaches within the grace window.
 *
 * Reactivity is fully preserved: Dexie writes flow through the live-query
 * observable, the cached value updates, and every current subscriber
 * re-renders.
 *
 * Keying contract: callers must produce a `key` that uniquely identifies
 * the query. Two callers passing the same `key` share one observable; the
 * `queryFactory` is only invoked on the first creation, so any captured
 * closure differences between callers with identical keys are ignored.
 *
 * Concurrent-mode safety: entry creation + observable subscription happen
 * inside the `subscribe` callback that React invokes during commit, not
 * inside the render body. If React abandons a render (Suspense throw,
 * higher-priority update preempting work-in-progress), no orphan
 * subscription is left behind because nothing was created yet.
 */

interface CacheEntry<T> {
  /** Latest value emitted by the observable, or `undefined` until first emit. */
  value: T | undefined
  hasValue: boolean
  observable: Observable<T>
  subscription: { unsubscribe(): void }
  /** React force-update callbacks registered by mounted hook instances. */
  subscribers: Set<() => void>
  /** Pending teardown timer fired after the last subscriber detaches. */
  destroyTimer: ReturnType<typeof setTimeout> | null
  /**
   * DB epoch this entry's observable was created under. A reopen bumps the
   * global epoch (see `db-epoch.ts`); an entry from an older epoch is bound to
   * a since-closed Dexie connection and must be rebuilt on the fresh one.
   */
  epoch: number
}

const cache = new Map<string, CacheEntry<unknown>>()

/**
 * How long an entry survives after its last subscriber unmounts. A
 * virtualizer scroll-out + scroll-back, or a collapsible toggle, completes
 * well under this window, so the cached value is reused with zero IDB
 * work. Tunable; longer windows trade memory for lower miss rate.
 */
const GRACE_MS = 30_000

function getOrCreate<T>(
  key: string,
  queryFactory: () => Promise<T> | T,
  epoch: number
): CacheEntry<T> {
  const existing = cache.get(key) as CacheEntry<T> | undefined
  // Reuse only if the entry was built under the CURRENT epoch. A stale-epoch
  // entry's observable is bound to a since-closed connection (cross-tab
  // logout/delete); tear it down and rebuild so the caller re-subscribes on
  // the reopened DB instead of reading empty forever.
  if (existing && existing.epoch === epoch) return existing
  if (existing) {
    existing.subscription.unsubscribe()
    if (existing.destroyTimer) clearTimeout(existing.destroyTimer)
    cache.delete(key)
  }
  const observable = liveQuery(queryFactory) as Observable<T>
  const entry: CacheEntry<T> = {
    value: undefined,
    hasValue: false,
    observable,
    subscription: null as unknown as { unsubscribe(): void },
    subscribers: new Set(),
    destroyTimer: null,
    epoch,
  }
  // Dexie 4's Observable.subscribe is RxJS-compatible and accepts an
  // observer object OR positional callbacks. Use positional form — it's
  // the primary documented signature and avoids observer-shape-mismatch
  // surprises across minor dexie versions.
  entry.subscription = observable.subscribe(
    (v) => {
      entry.value = v
      entry.hasValue = true
      for (const cb of entry.subscribers) cb()
    },
    (err: unknown) => {
      // Don't poison the cache — keep `hasValue` as it was so consumers
      // still see the last-good value while we surface the error.
      console.error(`[useSharedLiveQuery] ${key}`, err)
    }
  )
  cache.set(key, entry as CacheEntry<unknown>)
  return entry
}

function scheduleDestroy<T>(key: string, entry: CacheEntry<T>): void {
  if (entry.destroyTimer) clearTimeout(entry.destroyTimer)
  entry.destroyTimer = setTimeout(() => {
    // Re-check the subscriber count: a remount inside the grace window
    // would have cancelled this timer, but a remount-then-unmount before
    // the timer fires could leave the entry empty again.
    if (entry.subscribers.size === 0) {
      entry.subscription.unsubscribe()
      cache.delete(key)
    }
  }, GRACE_MS)
}

/**
 * Subscribe to a Dexie `liveQuery` whose observable is shared across all
 * components passing the same `key`. The cached value is read
 * synchronously via `useSyncExternalStore`'s snapshot path, so a remount
 * inside the grace window paints with data immediately — no `undefined`
 * → fetch → re-render churn.
 *
 * @param key  Identifier for the underlying observable. Two callers with
 *             the same key share the same observable; the `queryFactory`
 *             is only invoked on first creation.
 * @param queryFactory  Dexie query function. Invoked once per `key` (until
 *             the entry is torn down after the grace window). Held in a
 *             ref so the `subscribe` callback's identity stays stable
 *             across renders that hand in fresh-but-equivalent arrow
 *             functions.
 */
export function useSharedLiveQuery<T>(
  key: string,
  queryFactory: () => Promise<T> | T
): T | undefined {
  // Latest factory in a ref — `subscribe` closes over this and React
  // only re-subscribes when `key` changes, so we must read through the
  // ref to avoid running a stale factory on first creation.
  const queryFactoryRef = useRef(queryFactory)
  queryFactoryRef.current = queryFactory

  // A DB reopen bumps the epoch; threading it through `subscribe`'s deps makes
  // React re-subscribe, and `getOrCreate` then rebuilds the (stale-epoch)
  // shared observable on the fresh connection. Without it the cached observable
  // stays bound to the closed DB and every sharer reads empty until reload.
  const epoch = useSyncExternalStore(subscribeDbEpoch, getDbEpoch, getDbEpoch)

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      // Entry creation + observable.subscribe happen here, inside the
      // subscribe callback that React invokes during commit — never in
      // render. If a render is preempted in concurrent mode, no orphan
      // observable is left behind.
      const entry = getOrCreate(key, queryFactoryRef.current, epoch)
      if (entry.destroyTimer) {
        clearTimeout(entry.destroyTimer)
        entry.destroyTimer = null
      }
      entry.subscribers.add(onStoreChange)
      return () => {
        entry.subscribers.delete(onStoreChange)
        if (entry.subscribers.size === 0) {
          scheduleDestroy(key, entry)
        }
      }
    },
    [key, epoch]
  )

  const getSnapshot = useCallback((): T | undefined => {
    const entry = cache.get(key) as CacheEntry<T> | undefined
    return entry?.value
  }, [key])

  // `getSnapshot` is reused as `getServerSnapshot` so SSR / hydration
  // doesn't tear — the cache is module-level and empty in both phases.
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/**
 * Test/utility helper. Drops every live entry, cancelling subscriptions
 * and pending destroy timers. Lets tests reset module state between runs;
 * not intended for production use.
 */
export function __resetSharedLiveQueryCacheForTests(): void {
  for (const entry of cache.values()) {
    if (entry.destroyTimer) clearTimeout(entry.destroyTimer)
    entry.subscription?.unsubscribe()
  }
  cache.clear()
}
